import React, { useLayoutEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components/macro';

import formatNumber from 'helpers/formatNumber';
import { definitionLabel } from 'styles/base-classes';
import { Circle as BasicCircle } from 'components/base/KeyValuePair/KeyValuePair';

export const TooltipContainer = styled('div')<any>`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid ${({ theme }): string => theme.Grey};
  padding: ${({ theme }): string =>
    `${theme.spacing.xsmall} ${theme.spacing.small}`};
  position: absolute;
  white-space: nowrap;
  transform: ${({ theme }): string =>
    `translate(${theme.spacing.small}, -50%)`};
  pointer-events: none;
  text-align: ${({ xPosition }): string =>
    xPosition === 'left' ? 'right' : 'left'};
  min-width: 100px;

  ${(props): any => css`
    transform: ${getTooltipTransform(props)};
  `};
`;

export const TooltipLabel = styled('span')`
  ${definitionLabel};
`;

const TooltipValue = styled('span')`
  font-weight: 600;
  line-height: 1.2;
`;

const ValueWrapper = styled('div')``;

const Circle = styled(BasicCircle)`
  display: block;
  margin-top: 3px;
`;

const TitleWrapper = styled('div')`
  display: flex;
  align-items: center;
`;

const BlockWrapper = styled('div')`
  line-height: 1.2;
`;

const TooltipSecondValue = styled('span')`
  font-size: 11px;
  font-weight: 600;
  margin-left: 5px;
`;

function getTooltipTransform({ showAbove, xPosition, theme }: any): string {
  if (showAbove) {
    if (xPosition === 'left') {
      return `translate(-100%, calc(-${theme.spacing.small} - 100%))`;
    } else if (xPosition === 'center') {
      return `translate(-50%, calc(-${theme.spacing.small} - 100%))`;
    } else {
      return `translate(0, calc(-${theme.spacing.small} - 100%))`;
    }
  } else {
    if (xPosition === 'left') {
      return `translate(calc(-${theme.spacing.small} - 100%), -50%)`;
    } else {
      return `translate(${theme.spacing.small}, -50%)`;
    }
  }
}

interface ITrackerTooltipItemProps {
  /** The reference to the Tooltip portal container */
  tooltipRoot?: { current: Element };
  /** The label of the type of data. E.g. streams / downloads */
  labels: string[];
  /** The x position of the Tooltip */
  left: number;
  /** The y position of the Tooltip */
  top: number;
  /** The color of the TooltipValue */
  color: string;
  /** The value to display inside the Tooltip */
  yValue: any;
  /** If the user is on a touch device */
  hasTouch?: boolean;
  secondValue?: number[];
  /** If yValue should be formatted with letter prefix, for instance, 10k, 25M */
  isFormattedNumber: boolean;
  dotColors?: string[];
  type?: string;
}

/**
 * Displays a Tooltip on the Linechart that shows the value of the currently highlighted datapoint
 * The tooltip will position itself automatically based on the space available.
 */
const TrackerTooltipItem: React.FC<ITrackerTooltipItemProps> = (props) => {
  const {
    tooltipRoot,
    labels,
    left,
    top,
    color,
    yValue,
    hasTouch,
    secondValue,
    isFormattedNumber,
    dotColors = [],
    type,
  } = props;

  const tooltip = useRef(null) as any;
  const [xPosition, setXPosition] = useState('right');

  /** If the location of the tooltip changes, calculate the xPosition again. */
  useLayoutEffect(() => {
    /** Calculates whether the tooltip should show on the left or the right side of the tracker */
    function calculateXPosition(): 'left' | 'center' | 'right' {
      if (tooltipRoot && tooltipRoot.current && tooltip.current) {
        const rootWidth = tooltipRoot.current.getBoundingClientRect().width;
        const { width } = tooltip.current.getBoundingClientRect();

        if (hasTouch) {
          if (left + width / 2 > rootWidth) {
            return 'left';
          } else if (width / 2 > left) {
            return 'right';
          } else {
            return 'center';
          }
        } else {
          if (left + width > rootWidth) {
            return 'left';
          } else {
            return 'right';
          }
        }
      }

      return 'right';
    }

    setXPosition(calculateXPosition());
  }, [hasTouch, left, tooltipRoot, tooltip]);

  const formattedNumber = isFormattedNumber
    ? formatNumber(yValue, 'number', true)
    : formatNumber(yValue);

  return (
    <TooltipContainer
      xPosition={xPosition}
      showAbove={hasTouch}
      ref={tooltip}
      style={{
        left,
        top,
      }}
    >
      {type === 'orgVsProg' && secondValue ? (
        <>
          {labels.map((label, index) => (
            <BlockWrapper key={label}>
              <TitleWrapper>
                <Circle color={dotColors[index]} width="6px" height="6px" />
                <TooltipLabel>{label}</TooltipLabel>
              </TitleWrapper>
              <ValueWrapper>
                <TooltipValue style={{ color }}>
                  {formatNumber(secondValue[index], 'percentage')}
                </TooltipValue>
                <TooltipSecondValue style={{ color: '#8DA5BF' }}>
                  {formatNumber(yValue[index], 'number', true)}
                </TooltipSecondValue>
              </ValueWrapper>
            </BlockWrapper>
          ))}
        </>
      ) : (
        <>
          <TooltipLabel>{labels[0]}</TooltipLabel>
          <TooltipValue style={{ color }}>{formattedNumber}</TooltipValue>
        </>
      )}
    </TooltipContainer>
  );
};

export default TrackerTooltipItem;
